import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { scansApi, patientsApi, generationApi } from '../../lib/api';
import { InsufficientCreditsModal, isInsufficientCreditsError } from '../../components/credits/InsufficientCreditsModal';

import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Upload,
  Sparkles,
  Download,
  Image as ImageIcon,
  Wand2,
  FileText,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertCircle,
  Baby,
  Ruler,
  Eye,
  FileCheck,
  Layers,
  ShieldCheck,
  ShieldX,
  Scan,
  RefreshCw,
  Move3d,
  Camera,
  ScanLine
} from 'lucide-react';
import { ShareActions } from '../../components/shared/ShareActions';

// Biyometrik input alanları
// ==================== HADLOCK PARAMETRELERİ ====================
const HADLOCK_FIELDS = [
  { key: 'bpd', label: 'BPD (mm)', desc: 'Biparietal Diameter', min: 20, max: 100 },
  { key: 'ofd', label: 'OFD (mm)', desc: 'Occipitofrontal Diameter', min: 20, max: 120 },
  { key: 'hc', label: 'HC (mm)', desc: 'Head Circumference', min: 80, max: 400 },
  { key: 'ac', label: 'AC (mm)', desc: 'Abdominal Circumference', min: 50, max: 400 },
  { key: 'fl', label: 'FL (mm)', desc: 'Femur Length', min: 10, max: 80 },
];

// ==================== BEYİN PARAMETRELERİ ====================
const BRAIN_FIELDS = [
  { key: 'cerebellum', label: 'Cerebellum (mm)', desc: 'Serebellum', min: 5, max: 60 },
  { key: 'cisterna_magna', label: 'CM (mm)', desc: 'Cisterna Magna', min: 1, max: 15 },
  { key: 'nuchal_fold', label: 'NF (mm)', desc: 'Nuchal Fold', min: 1, max: 10 },
];

// ==================== YÜZ PARAMETRELERİ ====================
const FACE_FIELDS = [
  { key: 'fromen', label: 'Fromen (mm)', desc: 'Alin Genisligi', min: 10, max: 100 },
  { key: 'nasal_bone', label: 'Burun (mm)', desc: 'Nasal Bone Length', min: 2, max: 20 },
  { key: 'iod', label: 'Goztepe/IOD (mm)', desc: 'Inter-Orbital Distance', min: 5, max: 40 },
  { key: 'bioc_cap', label: 'BiocCap (mm)', desc: 'Biokuler Cap', min: 10, max: 50 },
  { key: 'chin', label: 'Cene (mm)', desc: 'Chin', min: 2, max: 15 },
  { key: 'mouth_diameter', label: 'Agiz Capi (mm)', desc: 'Mouth Diameter', min: 5, max: 30 },
  { key: 'eye', label: 'Goz (mm)', desc: 'Goz Olcumu', min: 3, max: 20 },
  { key: 'upper_lip', label: 'Ust Dudak (mm)', desc: 'Upper Lip', min: 2, max: 10 },
];

// ==================== JEANTY PARAMETRELERİ ====================
const JEANTY_FIELDS = [
  { key: 'humerus', label: 'HL (mm)', desc: 'Humerus Length', min: 10, max: 70 },
  { key: 'tibia', label: 'TIB (mm)', desc: 'Tibia', min: 10, max: 70 },
  { key: 'fibula', label: 'FIB (mm)', desc: 'Fibula', min: 10, max: 65 },
  { key: 'radius', label: 'RAD (mm)', desc: 'Radius', min: 10, max: 60 },
  { key: 'ulna', label: 'Ulna (mm)', desc: 'Ulna', min: 10, max: 65 },
];

const ALL_FIELD_GROUPS = [
  { title: 'Hadlock Parametreleri', fields: HADLOCK_FIELDS },
  { title: 'Beyin Parametreleri', fields: BRAIN_FIELDS },
  { title: 'Yuz Parametreleri', fields: FACE_FIELDS },
  { title: 'Jeanty Parametreleri (Kemik)', fields: JEANTY_FIELDS },
];

const BIOMETRIC_FIELDS = [...HADLOCK_FIELDS, ...BRAIN_FIELDS, ...FACE_FIELDS, ...JEANTY_FIELDS];

// SCAN ANİMASYONU - İki Görüntü BİRBİRİNE KARIŞIYOR / BİRLEŞİYOR
const ScanMergeAnimation = ({ sideImage, frontImage, isScanning, scanProgress, validationStatus }) => {
  const [mergeProgress, setMergeProgress] = useState(0);
  const [scanLine, setScanLine] = useState(0);
  
  // Birleşme animasyonu - görüntüler birbirine karışıyor
  useEffect(() => {
    if (!isScanning || !sideImage || !frontImage) {
      setMergeProgress(0);
      setScanLine(0);
      return;
    }
    
    // Yavaş birleşme efekti
    const mergeInterval = setInterval(() => {
      setMergeProgress(prev => {
        if (prev >= 100) return 100;
        return prev + 0.5;
      });
    }, 30);
    
    // Scan çizgisi
    const scanInterval = setInterval(() => {
      setScanLine(prev => {
        if (prev >= 100) return 0;
        return prev + 2;
      });
    }, 50);
    
    return () => {
      clearInterval(mergeInterval);
      clearInterval(scanInterval);
    };
  }, [isScanning, sideImage, frontImage]);

  const hasBothImages = sideImage && frontImage;
  
  // Birleşme oranı hesapla (0-1 arası)
  const blendRatio = mergeProgress / 100;

  return (
    <div className="relative w-full aspect-video bg-slate-950 rounded-xl overflow-hidden border border-cyan-500/30">
      {/* Grid arka plan */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(rgba(34, 211, 238, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '25px 25px'
        }}
      />
      
      {!sideImage && !frontImage ? (
        // Boş durum
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Layers className="w-16 h-16 text-cyan-500/40 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Ultrason görüntülerini yükleyin</p>
            <p className="text-cyan-500/60 text-xs mt-1">Profil + Frontal</p>
          </div>
        </div>
      ) : !hasBothImages ? (
        // Tek görüntü var
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <img
            src={sideImage || frontImage}
            alt="Ultrason"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
          <div className={`absolute bottom-3 ${sideImage ? 'left-3' : 'right-3'} px-3 py-1.5 rounded-md text-xs text-white font-medium ${sideImage ? 'bg-blue-600/90' : 'bg-emerald-600/90'}`}>
            {sideImage ? <><Eye className="w-3.5 h-3.5 inline mr-1" /> Profil</> : <><Baby className="w-3.5 h-3.5 inline mr-1" /> Frontal</>}
          </div>
        </div>
      ) : (
        // İKİ GÖRÜNTÜ BİRLEŞME ANİMASYONU
        <>
          {/* Alt katman - Profil görüntü (sabit) */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <img
              src={sideImage}
              alt="Profil"
              className="max-w-full max-h-full object-contain"
              style={{
                opacity: isScanning ? 1 - blendRatio * 0.5 : 1,
                filter: isScanning ? `blur(${blendRatio * 2}px)` : 'none',
                transform: isScanning ? `translateX(${-blendRatio * 10}%)` : 'none',
                transition: 'all 0.1s ease-out'
              }}
            />
          </div>
          
          {/* Üst katman - Frontal görüntü (blend ile karışıyor) */}
          <div 
            className="absolute inset-0 flex items-center justify-center p-4"
            style={{
              mixBlendMode: isScanning ? 'screen' : 'normal',
              opacity: isScanning ? blendRatio : 0
            }}
          >
            <img
              src={frontImage}
              alt="Frontal"
              className="max-w-full max-h-full object-contain"
              style={{
                filter: isScanning ? `blur(${(1 - blendRatio) * 3}px)` : 'none',
                transform: isScanning ? `translateX(${(1 - blendRatio) * 10}%)` : 'none',
                transition: 'all 0.1s ease-out'
              }}
            />
          </div>
          
          {/* Merkez birleşme efekti - parlak çizgi */}
          {isScanning && (
            <div 
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{
                left: `${50 - (50 - 50 * blendRatio)}%`,
                width: '4px',
                background: 'linear-gradient(to bottom, transparent, rgba(34, 211, 238, 0.9), rgba(245, 158, 11, 0.9), rgba(34, 211, 238, 0.9), transparent)',
                boxShadow: '0 0 30px rgba(34, 211, 238, 0.8), 0 0 60px rgba(245, 158, 11, 0.5)',
                opacity: 1 - blendRatio * 0.8
              }}
            />
          )}
          
          {/* Yatay tarama çizgisi */}
          {isScanning && (
            <div 
              className="absolute left-0 right-0 h-1 pointer-events-none"
              style={{
                top: `${scanLine}%`,
                background: 'linear-gradient(90deg, transparent, rgba(34, 211, 238, 1), transparent)',
                boxShadow: '0 0 20px rgba(34, 211, 238, 0.9), 0 0 40px rgba(34, 211, 238, 0.5)'
              }}
            />
          )}
          
          {/* Köşe parçacıkları - birleşme sırasında */}
          {isScanning && [...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1.5 h-1.5 rounded-full bg-cyan-400 pointer-events-none animate-ping"
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
                animationDelay: `${i * 0.1}s`,
                animationDuration: '1s',
                opacity: 0.6
              }}
            />
          ))}
          
          {/* İşlem tamamlanınca - birleşmiş görüntü göster */}
          {!isScanning && validationStatus?.isValid && (
            <div 
              className="absolute inset-0 flex items-center justify-center p-4"
              style={{ mixBlendMode: 'screen' }}
            >
              <img
                src={frontImage}
                alt="Birleşik"
                className="max-w-full max-h-full object-contain opacity-40"
              />
            </div>
          )}
          
          {/* Etiketler */}
          {!isScanning && (
            <>
              <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-blue-600/90 backdrop-blur rounded-md text-xs text-white font-medium">
                <Eye className="w-3.5 h-3.5 inline mr-1" /> Profil
              </div>
              <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-emerald-600/90 backdrop-blur rounded-md text-xs text-white font-medium">
                <Baby className="w-3.5 h-3.5 inline mr-1" /> Frontal
              </div>
            </>
          )}
        </>
      )}
      
      {/* Köşe çerçeveleri */}
      <div className="absolute top-2 left-2 w-8 h-8 border-l-2 border-t-2 border-cyan-500/50" />
      <div className="absolute top-2 right-2 w-8 h-8 border-r-2 border-t-2 border-cyan-500/50" />
      <div className="absolute bottom-2 left-2 w-8 h-8 border-l-2 border-b-2 border-cyan-500/50" />
      <div className="absolute bottom-2 right-2 w-8 h-8 border-r-2 border-b-2 border-cyan-500/50" />
      
      {/* Tarama durumu göstergesi */}
      {isScanning && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-5 py-2 bg-slate-900/95 border border-cyan-500/50 rounded-full shadow-lg shadow-cyan-500/30">
          <div className="flex items-center gap-3">
            <Scan className="w-5 h-5 text-cyan-400 animate-pulse" />
            <span className="text-cyan-400 text-sm font-mono">{scanProgress || `Birleştiriliyor... %${Math.round(mergeProgress)}`}</span>
          </div>
        </div>
      )}
      
      {/* Validasyon durumu */}
      {validationStatus && !isScanning && (
        <div className={`absolute top-4 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-full shadow-lg ${
          validationStatus.isValid 
            ? 'bg-emerald-600/95 border border-emerald-400/50' 
            : 'bg-red-600/95 border border-red-400/50'
        }`}>
          <div className="flex items-center gap-2 text-white text-sm font-medium">
            {validationStatus.isValid ? (
              <>
                <ShieldCheck className="w-5 h-5" />
                <span>GÖRÜNTÜLER DOĞRULANDI</span>
              </>
            ) : (
              <>
                <ShieldX className="w-5 h-5" />
                <span>{validationStatus.error || 'FARKLI BEBEKLER - REDDEDİLDİ'}</span>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* HUD bilgileri */}
      <div className="absolute top-3 left-3 text-[10px] text-cyan-400/60 font-mono">NEOBREED AI v2.0</div>
      <div className="absolute top-3 right-3 text-[10px] text-cyan-400/60 font-mono">{new Date().toLocaleTimeString('tr-TR')}</div>
    </div>
  );
};

// Görüntü Yükleme Kartı (Validasyon durumu ile - GA bilgisi dahil)
const ImageUploadCard = ({ 
  title, subtitle, image, onUpload, onRemove, 
  icon: Icon, color, disabled, isValidating, validation 
}) => (
  <div className={`relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
    <div className="text-center mb-2">
      <p className={`text-sm font-medium ${color}`}>{title}</p>
      <p className="text-xs text-slate-400">{subtitle}</p>
    </div>
    
    {image ? (
      <div className="relative group">
        <img
          src={image}
          alt={title}
          className={`w-full h-36 object-contain rounded-lg border-2 bg-slate-900 ${
            validation?.isValid ? 'border-green-400' : 
            validation?.isValid === false ? 'border-red-400' : 'border-slate-600'
          }`}
        />
        
        {/* Validasyon göstergesi */}
        {isValidating ? (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto" />
              <p className="text-xs text-white mt-1">Doğrulanıyor...</p>
            </div>
          </div>
        ) : validation ? (
          <div className={`absolute top-1 right-1 p-1 rounded-full ${
            validation.isValid ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {validation.isValid ? (
              <CheckCircle className="w-4 h-4 text-white" />
            ) : (
              <XCircle className="w-4 h-4 text-white" />
            )}
          </div>
        ) : null}
        
        {/* Silme butonu */}
        <button
          onClick={onRemove}
          className="absolute top-1 left-1 p-1 bg-red-500/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <XCircle className="w-4 h-4" />
        </button>
        
        {/* Validasyon detayı - GA bilgisi dahil */}
        {validation && !isValidating && (
          <div className={`absolute bottom-0 left-0 right-0 p-2 text-xs ${
            validation.isValid ? 'bg-green-500/90' : 'bg-red-500/90'
          } text-white rounded-b-lg`}>
            {validation.isValid ? (
              <div>
                <span>✓ {validation.face_view === 'profile' ? 'Profil' : validation.face_view === 'frontal' ? 'Frontal' : ''} görünüm</span>
                {validation.gestational_age && validation.gestational_age !== 'not_found' && (
                  <span className="ml-1 font-bold">• GA: {validation.gestational_age}</span>
                )}
              </div>
            ) : (
              <span>✗ {validation.issues?.[0] || 'Geçersiz görüntü'}</span>
            )}
          </div>
        )}
      </div>
    ) : (
      <label className="block cursor-pointer">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onUpload}
          className="hidden"
        />
        <div className={`w-full h-36 border-2 border-dashed rounded-lg flex flex-col items-center justify-center hover:border-amber-500 transition-colors bg-slate-800/50 ${color.replace('text-', 'border-')}`}>
          <Icon className={`w-10 h-10 ${color} mb-2`} />
          <span className="text-xs text-slate-400">Yüklemek için tıkla</span>
        </div>
      </label>
    )}
  </div>
);

export const Ultrasound4KPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('scanId');
  const initialJobId = searchParams.get('jobId');

  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState(null);
  const [patient, setPatient] = useState(null);
  
  // Görüntüler
  const [sideImage, setSideImage] = useState(null);
  const [frontImage, setFrontImage] = useState(null);
  
  // Validasyon durumları
  const [sideValidation, setSideValidation] = useState(null);
  const [frontValidation, setFrontValidation] = useState(null);
  const [isValidatingSide, setIsValidatingSide] = useState(false);
  const [isValidatingFront, setIsValidatingFront] = useState(false);
  const [pairValidation, setPairValidation] = useState(null);
  
  // Sonuçlar
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhanceProgress, setEnhanceProgress] = useState('');
  
  // Tabs
  const [activeTab, setActiveTab] = useState('upload');

  // Credit system removed — generation is unrestricted.
  const hasNoCredits = false;

  // Step 8: HTTP 402 INSUFFICIENT_CREDITS modal state.
  const [insufficientCredits, setInsufficientCredits] = useState(null);
  
  // Biyometrik veriler
  const [biometrics, setBiometrics] = useState({
    bpd: 60, hc: 220, ac: 200, fl: 45, ofd: 75, nasal_bone: 8, iod: 18
  });
  
  // Analiz sonuçları
  const [validation, setValidation] = useState(null);
  const [pdfReport, setPdfReport] = useState(null);

  // OCR durumu
  const [ocrImage, setOcrImage] = useState(null);
  const [isExtractingOcr, setIsExtractingOcr] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrHistory, setOcrHistory] = useState([]); // Multi-photo history
  const ocrInputRef = useRef(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!scanId) {
        navigate('/doctor/patients');
        return;
      }
      try {
        const scanData = await scansApi.get(scanId);
        setScan(scanData);
        
        if (scanData.metadata?.side_image) setSideImage(scanData.metadata.side_image);
        if (scanData.metadata?.front_image) setFrontImage(scanData.metadata.front_image);
        if (scanData.metadata?.enhanced_image) setEnhancedImage(scanData.metadata.enhanced_image);
        if (scanData.metadata?.validation) setValidation(scanData.metadata.validation);
        
        // Load result from dedicated endpoint (handles both job_id and patient_id fallback)
        if (scanData.status === 'generated' && !scanData.metadata?.enhanced_image) {
          try {
            const result = await scansApi.getResult(scanId);
            if (result.found) {
              if (result.enhanced_image) setEnhancedImage(result.enhanced_image);
              if (result.validation) setValidation(result.validation);
              if (result.pdf_report) setPdfReport(result.pdf_report);
            }
          } catch (_) {}
        }
        
        // Biyometrik olcumleri yukle
        if (scanData.metadata?.biometrics) {
          const savedBio = scanData.metadata.biometrics;
          // Map Turkish UI keys to backend keys if needed
          const uiToBackendMap = {
            burun: 'nasal_bone', goztepe: 'iod', goz: 'eye_diameter',
            agiz_capi: 'mouth_diameter', ust_dudak: 'upper_lip', cene: 'chin'
          };
          const mappedBio = {};
          Object.entries(savedBio).forEach(([k, v]) => {
            if (k === 'cinsiyet') return;
            const backendKey = uiToBackendMap[k] || k;
            const numVal = parseFloat(v);
            if (!isNaN(numVal) && numVal > 0) mappedBio[backendKey] = numVal;
          });
          setBiometrics(prev => ({ ...prev, ...mappedBio }));
        }
        
        // Tamamlanmış analiz varsa direkt sonuçlar sekmesine geç
        const isCompleted = scanData.status === 'generated';
        if (isCompleted) {
          setActiveTab('results');
        }
        
        // Validasyon durumlarını yükle
        if (scanData.metadata?.side_validation) {
          setSideValidation({
            isValid: scanData.metadata.side_validation.is_valid,
            face_view: scanData.metadata.side_validation.face_view,
            issues: scanData.metadata.side_validation.issues,
            quality: scanData.metadata.side_validation.quality_score
          });
        } else if (scanData.metadata?.side_image && !isCompleted) {
          // Sadece tamamlanmamış analizlerde auto-validate yap
          setIsValidatingSide(true);
          try {
            const result = await generationApi.validateUltrasoundImage(scanData.metadata.side_image);
            setSideValidation({
              isValid: result.is_valid,
              face_view: result.face_view,
              issues: result.issues,
              quality: result.quality_score
            });
            await scansApi.update(scanId, {
              metadata: { side_validation: result }
            });
          } catch (err) {
            setSideValidation({ isValid: true, issues: [] });
          } finally {
            setIsValidatingSide(false);
          }
        } else if (scanData.metadata?.side_image && isCompleted) {
          // Tamamlanmış için varsayılan valid kabul et
          setSideValidation({ isValid: true, issues: [] });
        }
        
        if (scanData.metadata?.front_validation) {
          setFrontValidation({
            isValid: scanData.metadata.front_validation.is_valid,
            face_view: scanData.metadata.front_validation.face_view,
            issues: scanData.metadata.front_validation.issues,
            quality: scanData.metadata.front_validation.quality_score
          });
        } else if (scanData.metadata?.front_image && !isCompleted) {
          setIsValidatingFront(true);
          try {
            const result = await generationApi.validateUltrasoundImage(scanData.metadata.front_image);
            setFrontValidation({
              isValid: result.is_valid,
              face_view: result.face_view,
              issues: result.issues,
              quality: result.quality_score
            });
            await scansApi.update(scanId, {
              metadata: { front_validation: result }
            });
          } catch (err) {
            setFrontValidation({ isValid: true, issues: [] });
          } finally {
            setIsValidatingFront(false);
          }
        } else if (scanData.metadata?.front_image && isCompleted) {
          setFrontValidation({ isValid: true, issues: [] });
        }

        if (scanData.patient_id) {
          const patientData = await patientsApi.get(scanData.patient_id);
          setPatient(patientData);
          if (patientData.pregnancy_week) {
            const week = patientData.pregnancy_week;
            setBiometrics(prev => ({
              ...prev,
              bpd: Math.round(week * 2.5),
              hc: Math.round(week * 10),
              ac: Math.round(week * 9),
              fl: Math.round(week * 2),
              ofd: Math.round(week * 3.1)
            }));
          }
        }
      } catch (error) {
        toast.error('Veri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [scanId, navigate]);

  // Auto-poll if jobId is in URL (from Quick Exam wizard)
  useEffect(() => {
    if (!initialJobId || enhancedImage) return;
    
    let cancelled = false;
    const pollJob = async () => {
      setEnhancing(true);
      setActiveTab('results');
      setEnhanceProgress('Uretim devam ediyor...');
      
      const maxAttempts = 90;
      let attempts = 0;
      
      while (attempts < maxAttempts && !cancelled) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
        
        try {
          const status = await generationApi.getScientificUltrasound4KStatus(initialJobId);
          if (cancelled) break;
          setEnhanceProgress(status.progress || `Isleniyor... (%${Math.min(Math.round(attempts/maxAttempts*100), 95)})`);
          
          if (status.status === 'completed') {
            setEnhancedImage(status.enhanced_image);
            setValidation(status.validation);
            setPdfReport(status.pdf_report);
            
            if (scanId) {
              try {
                await scansApi.update(scanId, {
                  status: 'generated'
                });
              } catch (_) {}
            }
            
            toast.success('4K uretim tamamlandi!');
            break;
          } else if (status.status === 'failed') {
            toast.error(status.error || 'Uretim basarisiz');
            break;
          }
        } catch (error) {
          if (error?.status === 404) {
            toast.error('Islem bulunamadi');
            break;
          }
        }
      }
      
      if (!cancelled) {
        setEnhancing(false);
        setEnhanceProgress('');
      }
    };
    
    pollJob();
    return () => { cancelled = true; };
  }, [initialJobId, enhancedImage, scanId]);


  // Görüntü yükleme ve validasyon
  const handleImageUpload = useCallback((type) => async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görüntü dosyası seçin');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      
      if (type === 'side') {
        setSideImage(base64);
        setSideValidation(null);
        setIsValidatingSide(true);
      } else {
        setFrontImage(base64);
        setFrontValidation(null);
        setIsValidatingFront(true);
      }
      
      // Önceki sonuçları temizle
      setEnhancedImage(null);
      setValidation(null);
      setPairValidation(null);
      
      // AI ile doğrula
      try {
        const result = await generationApi.validateUltrasoundImage(base64);
        
        if (type === 'side') {
          setSideValidation({
            isValid: result.is_valid,
            face_view: result.face_view,
            issues: result.issues,
            quality: result.quality_score
          });
          setIsValidatingSide(false);
          
          if (result.is_valid) {
            toast.success(`Profil görüntü doğrulandı (Kalite: %${result.quality_score})`);
          } else {
            toast.error(result.issues?.[0] || 'Geçersiz görüntü');
          }
        } else {
          setFrontValidation({
            isValid: result.is_valid,
            face_view: result.face_view,
            issues: result.issues,
            quality: result.quality_score
          });
          setIsValidatingFront(false);
          
          if (result.is_valid) {
            toast.success(`Frontal görüntü doğrulandı (Kalite: %${result.quality_score})`);
          } else {
            toast.error(result.issues?.[0] || 'Geçersiz görüntü');
          }
        }
        
        // Save only validation result (not the base64 image)
        await scansApi.update(scanId, {
          metadata: type === 'side' 
            ? { side_validation: result }
            : { front_validation: result }
        });
        
      } catch (error) {
        const errorValidation = { isValid: false, issues: ['Doğrulama hatası'] };
        if (type === 'side') {
          setSideValidation(errorValidation);
          setIsValidatingSide(false);
        } else {
          setFrontValidation(errorValidation);
          setIsValidatingFront(false);
        }
        toast.error('Görüntü doğrulanamadı');
      }
    };
    reader.readAsDataURL(file);
  }, [scanId]);

  // OTOMATİK İKİLİ DOĞRULAMA - Her iki görüntü de yüklenip doğrulandığında otomatik çalışır
  useEffect(() => {
    const runAutoPairValidation = async () => {
      // Her iki görüntü var mı?
      if (!sideImage || !frontImage) return;
      
      // Her iki doğrulama tamamlandı mı?
      if (!sideValidation?.isValid || !frontValidation?.isValid) return;
      
      // Zaten validasyon yapıldı mı veya devam ediyor mu?
      if (pairValidation || enhancing) return;
      
      // Otomatik karşılaştırma başlat
      setEnhancing(true);
      setEnhanceProgress('Görüntüler otomatik karşılaştırılıyor...');
      
      try {
        const result = await generationApi.validateImagePair(sideImage, frontImage);
        setPairValidation(result);
        
        if (result.same_baby) {
          toast.success(`✅ Aynı bebek onaylandı! (Uyum: %${result.confidence})`, { duration: 4000 });
        } else {
          if (result.rejection_reason) {
            toast.error(`❌ ${result.rejection_reason}`, { duration: 6000 });
          } else {
            toast.error('❌ Farklı bebekler - Üretim engellendi', { duration: 5000 });
          }
        }
      } catch (error) {
        toast.error('Karşılaştırma hatası');
      } finally {
        setEnhancing(false);
        setEnhanceProgress('');
      }
    };
    
    runAutoPairValidation();
  }, [sideImage, frontImage, sideValidation, frontValidation, pairValidation, enhancing]);

  // Manuel karşılaştırma (artık sadece tekrar kontrol için)
  const handleValidatePair = useCallback(async () => {
    if (!sideImage || !frontImage) {
      toast.error('Her iki görüntüyü de yükleyin');
      return;
    }
    
    setEnhancing(true);
    setEnhanceProgress('İki görüntü karşılaştırılıyor...');
    
    try {
      const result = await generationApi.validateImagePair(sideImage, frontImage);
      setPairValidation(result);
      
      if (result.same_baby) {
        toast.success(`Görüntüler eşleşti! (Güven: %${result.confidence})`);
      } else {
        // GA farkı varsa özel hata mesajı göster
        if (result.rejection_reason) {
          toast.error(result.rejection_reason, { duration: 6000 });
        } else if (result.image1_ga && result.image2_ga) {
          toast.error(`FARKLI BEBEKLER! GA: ${result.image1_ga} vs ${result.image2_ga}`, { duration: 6000 });
        } else {
          toast.error('Görüntüler farklı bebeklere ait - Eşleşme başarısız', { duration: 5000 });
        }
      }
    } catch (error) {
      toast.error('Karşılaştırma hatası: ' + (error.message || ''));
    } finally {
      setEnhancing(false);
      setEnhanceProgress('');
    }
  }, [sideImage, frontImage]);

  // Bilimsel analiz başlat
  const handleScientificEnhance = useCallback(async () => {
    if (!sideImage && !frontImage) {
      toast.error('En az bir görüntü yükleyin');
      return;
    }
    
    // Validasyon kontrolü
    if (sideImage && !sideValidation?.isValid) {
      toast.error('Profil görüntü doğrulanmadı');
      return;
    }
    if (frontImage && !frontValidation?.isValid) {
      toast.error('Frontal görüntü doğrulanmadı');
      return;
    }
    
    // İKİ GÖRÜNTÜ VARSA MUTLAKA EŞLEŞMELİ - KRİTİK KONTROL
    if (sideImage && frontImage) {
      // Eğer karşılaştırma yapılmadıysa, şimdi yap
      if (!pairValidation) {
        setEnhancing(true);
        setEnhanceProgress('Görüntüler karşılaştırılıyor...');
        
        try {
          const pairResult = await generationApi.validateImagePair(sideImage, frontImage);
          setPairValidation(pairResult);
          
          if (!pairResult.same_baby) {
            setEnhancing(false);
            setEnhanceProgress('');
            if (pairResult.rejection_reason) {
              toast.error(`❌ ${pairResult.rejection_reason}`, { duration: 6000 });
            } else {
              toast.error('❌ Farklı bebekler - Üretim engellendi', { duration: 5000 });
            }
            return;
          }
          
          if (pairResult.confidence < 60) {
            setEnhancing(false);
            setEnhanceProgress('');
            toast.error(`Eşleşme güveni çok düşük (%${pairResult.confidence}). Üretim yapılamaz.`, { duration: 5000 });
            return;
          }
          
          toast.success(`✅ Görüntüler eşleşti! (Uyum: %${pairResult.confidence})`, { duration: 3000 });
          // Devam et - karşılaştırma başarılı
          
        } catch (error) {
          setEnhancing(false);
          setEnhanceProgress('');
          toast.error('Karşılaştırma hatası');
          return;
        }
      } else {
        // Karşılaştırma zaten yapıldı, sonucu kontrol et
        if (!pairValidation.same_baby) {
          toast.error('Görüntüler farklı bebeklere ait! Üretim yapılamaz.', { duration: 5000 });
          return;
        }
        if (pairValidation.confidence < 60) {
          toast.error(`Eşleşme güveni çok düşük (%${pairValidation.confidence}). Üretim yapılamaz.`, { duration: 5000 });
          return;
        }
      }
    }

    setEnhancing(true);
    setEnhanceProgress('Bilimsel analiz başlatılıyor...');
    
    try {
      let result;
      try {
        result = await generationApi.startScientificUltrasound4K({
          image: sideImage || frontImage,
          side_image: sideImage,
          front_image: frontImage,
          patient_id: patient?.id || scanId,
          scan_id: scanId,
          pregnancy_week: patient?.pregnancy_week || 24,
          gender: patient?.gender || 'female',
          biometrics: biometrics,
          view_type: 'frontal'
        });
      } catch (startErr) {
        // Backend returns HTTP 402 with INSUFFICIENT_CREDITS — open modal so
        // the user can redeem a code or request a package without leaving.
        if (isInsufficientCreditsError(startErr)) {
          setInsufficientCredits(startErr.detail);
          setEnhancing(false);
          return;
        }
        throw startErr;
      }
      
      const jobId = result.job_id;
      const maxAttempts = 60;
      let attempts = 0;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
        
        try {
          const status = await generationApi.getScientificUltrasound4KStatus(jobId);
          setEnhanceProgress(status.progress || 'İşleniyor...');
          
          if (status.status === 'completed') {
            setEnhancedImage(status.enhanced_image);
            setValidation(status.validation);
            setPdfReport(status.pdf_report);
            
            await scansApi.update(scanId, {
              status: 'generated'
            });
            
            toast.success('Bilimsel analiz tamamlandı!');
            setActiveTab('results');
            break;
          } else if (status.status === 'failed') {
            toast.error(status.error || 'İşlem başarısız');
            break;
          }
        } catch (error) {
          console.error('Status check error:', error);
          if (error?.status === 404) {
            toast.error('İşlem bulunamadı. Sayfa yenilenmiş olabilir, lütfen tekrar başlatın.');
            break;
          }
        }
      }
    } catch (error) {
      toast.error('İşlem hatası: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setEnhancing(false);
      setEnhanceProgress('');
    }
  }, [sideImage, frontImage, sideValidation, frontValidation, patient, biometrics, scanId]);

  // Download
  const handleDownloadImage = useCallback(() => {
    if (!enhancedImage) return;
    const link = document.createElement('a');
    link.href = enhancedImage;
    link.download = `ultrason-4k-${patient?.first_name || 'bebek'}-${new Date().toISOString().split('T')[0]}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Görüntü indirildi');
  }, [enhancedImage, patient]);

  const handleDownloadReport = useCallback(() => {
    if (!pdfReport) return;
    const link = document.createElement('a');
    link.href = pdfReport;
    link.download = `ultrason-4k-rapor-${patient?.first_name || 'bebek'}-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Rapor indirildi');
  }, [pdfReport, patient]);

  // OCR - Fotoğraftan biyometrik ölçüm çıkarma (ÇOKLU FOTOĞRAF DESTEĞİ)
  const handleOcrCapture = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görüntü dosyası seçin');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      setOcrImage(base64);
      setIsExtractingOcr(true);
      setOcrResult(null);

      try {
        const result = await generationApi.extractBiometrics(base64);
        setOcrResult(result);

        if (result.success) {
          // MERGE: Sadece DOLU gelen değerleri ekle, mevcut DOLU değerleri KORUMA
          const updates = {};
          let filledCount = 0;
          
          // Tüm olası alanları kontrol et
          const allKeys = BIOMETRIC_FIELDS.map(f => f.key);
          for (const key of allKeys) {
            if (result[key] != null && result[key] !== 0) {
              // Sadece boş/0 olan alanlara yaz VEYA mevcut değeri güncelle
              updates[key] = result[key];
              filledCount++;
            }
          }

          if (filledCount > 0) {
            setBiometrics(prev => {
              const merged = { ...prev };
              for (const [key, val] of Object.entries(updates)) {
                // Mevcut değer boş veya 0 ise yeni değeri yaz, değilse yeni olanı al
                if (!merged[key] || merged[key] === 0) {
                  merged[key] = val;
                } else {
                  merged[key] = val; // Her zaman en son okunanı al
                }
              }
              return merged;
            });

            // History'ye ekle
            setOcrHistory(prev => [...prev, {
              image: base64,
              source_type: result.source_type,
              confidence: result.confidence,
              filledCount,
              timestamp: new Date().toLocaleTimeString('tr-TR'),
            }]);

            toast.success(`${filledCount} ölçüm okundu (Foto ${ocrHistory.length + 1}) - Güven: %${result.confidence || '?'}`, { duration: 4000 });
          } else {
            toast.warning('Görüntüde biyometrik ölçüm bulunamadı');
          }
        } else {
          toast.error(result.error || 'OCR başarısız');
        }
      } catch (err) {
        toast.error('Ölçüm çıkarma hatası: ' + (err.message || ''));
        setOcrResult({ success: false, error: err.message });
      } finally {
        setIsExtractingOcr(false);
        // Input'u sıfırla (aynı dosya tekrar seçilebilsin)
        if (ocrInputRef.current) ocrInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  }, [ocrHistory.length]);

  // Helpers
  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getStatusIcon = (status) => {
    if (status === 'match') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'close') return <AlertCircle className="w-4 h-4 text-amber-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#F59E0B]" />
        </div>
      </DashboardLayout>
    );
  }

  const hasAnyImage = sideImage || frontImage;
  const hasBothImages = sideImage && frontImage;
  const allValidated = (!sideImage || sideValidation?.isValid) && (!frontImage || frontValidation?.isValid);

  return (
    <DashboardLayout>
      <div className="h-full flex flex-col p-4 gap-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
                <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-[#F59E0B]" />
                Ultrason 4K - Bilimsel Analiz
              </h1>
              {patient && (
                <p className="text-xs md:text-sm text-slate-500">
                  {patient.first_name} {patient.last_name} • {patient.pregnancy_week || '?'}. hafta
                </p>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {enhancedImage && (
              <>
                <Button onClick={handleDownloadImage} variant="outline" size="sm" data-testid="download-image-btn">
                  <Download className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Görüntü</span>
                </Button>
                <Button 
                  onClick={() => {
                    sessionStorage.setItem('4d_source_image', enhancedImage);
                    navigate('/doctor/4d-simulation?type=ultrasound_4k');
                  }}
                  size="sm"
                  className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white"
                  data-testid="go-4d-from-4k"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Video
                </Button>
                <Button
                  onClick={() => {
                    sessionStorage.setItem('ar_360_image', enhancedImage);
                    navigate('/doctor/ar-360');
                  }}
                  variant="outline"
                  size="sm"
                  data-testid="go-ar360-from-4k"
                >
                  <Move3d className="w-4 h-4 mr-1" />
                  360°
                </Button>
              </>
            )}
            {pdfReport && (
              <Button onClick={handleDownloadReport} size="sm" className="bg-[#F59E0B] hover:bg-[#D97706]" data-testid="download-pdf-btn">
                <FileText className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">PDF Rapor</span>
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="bg-slate-100 mb-4">
            <TabsTrigger value="upload" className="data-[state=active]:bg-white">
              <Layers className="w-4 h-4 mr-2" />
              Görüntüler
            </TabsTrigger>
            <TabsTrigger value="biometrics" disabled={!hasAnyImage || !allValidated} className="data-[state=active]:bg-white">
              <Ruler className="w-4 h-4 mr-2" />
              Biyometri
            </TabsTrigger>
            <TabsTrigger value="results" disabled={!enhancedImage} className="data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Sonuçlar
            </TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload" className="flex-1">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 h-full">
              {/* Sol - Görüntü yükleme */}
              <div className="lg:col-span-2 space-y-4">
                <Card className="border-0 shadow-lg">
                  <CardHeader className="py-3 px-4 border-b bg-slate-50">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Ultrason Görüntüleri
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <ImageUploadCard
                        title="Profil Görünüm"
                        subtitle="Yandan ultrason"
                        image={sideImage}
                        onUpload={handleImageUpload('side')}
                        onRemove={() => { setSideImage(null); setSideValidation(null); }}
                        icon={Eye}
                        color="text-blue-500"
                        isValidating={isValidatingSide}
                        validation={sideValidation}
                      />
                      <ImageUploadCard
                        title="Frontal Görünüm"
                        subtitle="Önden ultrason"
                        image={frontImage}
                        onUpload={handleImageUpload('front')}
                        onRemove={() => { setFrontImage(null); setFrontValidation(null); }}
                        icon={Baby}
                        color="text-green-500"
                        isValidating={isValidatingFront}
                        validation={frontValidation}
                      />
                    </div>
                    
                    {/* Validasyon durumu */}
                    {hasAnyImage && (
                      <div className={`p-3 rounded-lg border ${
                        allValidated ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
                      }`}>
                        <p className={`text-xs ${allValidated ? 'text-green-700' : 'text-amber-700'}`}>
                          {allValidated ? (
                            <>
                              <ShieldCheck className="w-4 h-4 inline mr-1" />
                              Görüntüler doğrulandı! Biyometri sekmesine geçebilirsiniz.
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4 inline mr-1" />
                              Görüntüler doğrulanıyor veya geçersiz...
                            </>
                          )}
                        </p>
                      </div>
                    )}
                    
                    {/* İki görüntü karşılaştırma */}
                    {/* Otomatik doğrulama yapılıyor göstergesi */}
                    {hasBothImages && allValidated && !pairValidation && enhancing && (
                      <div className="flex items-center justify-center gap-2 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                        <Loader2 className="w-5 h-5 text-cyan-600 animate-spin" />
                        <span className="text-sm text-cyan-700 font-medium">Görüntüler otomatik karşılaştırılıyor...</span>
                      </div>
                    )}
                    
                    {/* Tekrar kontrol butonu - sadece sonuç varsa göster */}
                    {pairValidation && (
                      <Button
                        onClick={() => { setPairValidation(null); }}
                        variant="ghost"
                        size="sm"
                        className="w-full text-slate-500 hover:text-slate-700"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Tekrar Kontrol Et
                      </Button>
                    )}
                    
                    {pairValidation && (
                      <div className={`p-3 rounded-lg border ${
                        pairValidation.same_baby 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-red-50 border-red-300'
                      }`}>
                        <p className={`text-sm font-bold ${
                          pairValidation.same_baby ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {pairValidation.same_baby 
                            ? `✓ Aynı bebek (Uyum: %${pairValidation.confidence})`
                            : '✗ FARKLI BEBEKLER - Üretim Engellendi!'
                          }
                        </p>
                        
                        {/* MATEMATİKSEL DOĞRULAMA TABLOSU */}
                        {pairValidation.verification_table && pairValidation.verification_table.length > 0 && (
                          <div className="mt-3 bg-white rounded-lg overflow-hidden border">
                            <div className="bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700 border-b">
                              📊 Yüz Geometrisi Karşılaştırma Tablosu
                            </div>
                            <table className="w-full text-[10px]">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="px-2 py-1 text-left font-medium text-slate-600">Parametre</th>
                                  <th className="px-2 py-1 text-center font-medium text-blue-600">Görüntü 1</th>
                                  <th className="px-2 py-1 text-center font-medium text-green-600">Görüntü 2</th>
                                  <th className="px-2 py-1 text-center font-medium text-slate-600">Fark</th>
                                  <th className="px-2 py-1 text-center font-medium text-slate-600">Tolerans</th>
                                  <th className="px-2 py-1 text-center font-medium text-slate-600">Sonuç</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pairValidation.verification_table.map((row, idx) => (
                                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                    <td className="px-2 py-1 font-medium text-slate-700">{row.param}</td>
                                    <td className="px-2 py-1 text-center text-blue-700">{row.img1}</td>
                                    <td className="px-2 py-1 text-center text-green-700">{row.img2}</td>
                                    <td className="px-2 py-1 text-center text-slate-600">{row.diff}</td>
                                    <td className="px-2 py-1 text-center text-slate-500">{row.tolerance}</td>
                                    <td className="px-2 py-1 text-center">{row.result}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className={`px-2 py-1.5 text-xs font-bold text-center border-t ${
                              pairValidation.same_baby ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              TOPLAM UYUM: %{pairValidation.confidence} {pairValidation.same_baby ? '✅' : '❌'}
                            </div>
                          </div>
                        )}
                        
                        {/* Yüz özellikleri detayı (tablo yoksa) */}
                        {!pairValidation.verification_table && pairValidation.facial_features && (
                          <div className="mt-2 text-xs grid grid-cols-2 gap-2">
                            <div className="bg-blue-50 p-2 rounded">
                              <p className="font-bold text-blue-700 mb-1">Profil:</p>
                              {pairValidation.facial_features.image1 && (
                                <>
                                  <p>Burun: {pairValidation.facial_features.image1.nose_angle}°</p>
                                  <p>Alın: {pairValidation.facial_features.image1.forehead_slope}°</p>
                                  <p>GA: {pairValidation.facial_features.image1.ga || '-'}</p>
                                </>
                              )}
                            </div>
                            <div className="bg-green-50 p-2 rounded">
                              <p className="font-bold text-green-700 mb-1">Frontal:</p>
                              {pairValidation.facial_features.image2 && (
                                <>
                                  <p>Burun: {pairValidation.facial_features.image2.nose_angle}°</p>
                                  <p>Alın: {pairValidation.facial_features.image2.forehead_slope}°</p>
                                  <p>GA: {pairValidation.facial_features.image2.ga || '-'}</p>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Rejection reason */}
                        {pairValidation.rejection_reason && (
                          <p className="text-xs text-red-700 mt-2 font-bold bg-red-100 p-2 rounded">
                            ⚠️ {pairValidation.rejection_reason}
                          </p>
                        )}
                        
                        {pairValidation.notes && !pairValidation.rejection_reason && (
                          <p className="text-xs text-slate-600 mt-2 italic">{pairValidation.notes}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Kredi uyarı bandı */}
                    {hasAnyImage && allValidated && hasNoCredits && (
                      <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800" data-testid="credit-warning-banner-1">
                        <strong>Yetersiz kredi.</strong> 4K üretim için bir paket satın almanız gerekiyor.
                        Lütfen kliniğinizle iletişime geçin veya süper admin'e başvurun.
                      </div>
                    )}
                    {/* Devam butonu */}
                    {hasAnyImage && allValidated && (
                      <Button
                        onClick={handleScientificEnhance}
                        disabled={enhancing || hasNoCredits}
                        className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] h-12 text-lg disabled:opacity-60"
                        data-testid="scientific-enhance-btn-1"
                      >
                        {enhancing ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            {enhanceProgress}
                          </>
                        ) : hasNoCredits ? (
                          <>
                            <Wand2 className="w-5 h-5 mr-2" />
                            Kredi Yetersiz
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-5 h-5 mr-2" />
                            Bilimsel Analiz Başlat
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sağ - Scan animasyonu */}
              <div className="lg:col-span-3">
                <Card className="border-0 shadow-lg h-full">
                  <CardHeader className="py-3 px-4 border-b bg-gradient-to-r from-slate-800 to-slate-900">
                    <CardTitle className="text-sm flex items-center gap-2 text-white">
                      <Scan className="w-4 h-4 text-cyan-400" />
                      Birleşik Analiz Önizleme
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 bg-slate-900">
                    <ScanMergeAnimation
                      sideImage={sideImage}
                      frontImage={frontImage}
                      isScanning={isValidatingSide || isValidatingFront || enhancing}
                      scanProgress={enhanceProgress}
                      validationStatus={allValidated && hasAnyImage ? { isValid: true } : null}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Biometrics Tab */}
          <TabsContent value="biometrics" className="flex-1">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="border-0 shadow-lg lg:col-span-2">
                <CardHeader className="py-3 px-4 border-b bg-slate-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Ruler className="w-4 h-4 text-[#5A67D8]" />
                      Biyometrik Ölçümler
                    </CardTitle>
                    {/* OCR Kamera Butonu */}
                    <div className="flex items-center gap-2">
                      <input
                        ref={ocrInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleOcrCapture}
                        className="hidden"
                        data-testid="ocr-file-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => ocrInputRef.current?.click()}
                        disabled={isExtractingOcr}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="ocr-camera-btn"
                      >
                        {isExtractingOcr ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            Okunuyor...
                          </>
                        ) : (
                          <>
                            <Camera className="w-4 h-4 mr-1.5" />
                            Fotoğraftan Oku
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {/* OCR Sonuç Bildirimi + Çoklu Fotoğraf Geçmişi */}
                  {ocrHistory.length > 0 && (
                    <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="ocr-photo-history">
                      {ocrHistory.map((h, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-100 text-green-700 text-[10px] font-medium">
                          <img src={h.image} alt="" className="w-5 h-5 rounded-full object-cover" />
                          Foto {i + 1}: {h.filledCount} alan ({h.source_type === 'handwritten' ? 'El yazisi' : 'Makine'})
                        </div>
                      ))}
                    </div>
                  )}

                  {ocrResult && (
                    <div className={`mb-4 p-3 rounded-lg border text-sm ${
                      ocrResult.success 
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`} data-testid="ocr-result-banner">
                      <div className="flex items-start gap-2">
                        {ocrResult.success ? (
                          <ScanLine className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          {ocrResult.success ? (
                            <>
                              <p className="font-medium">
                                Ölçümler okundu (Güven: %{ocrResult.confidence || '?'})
                              </p>
                              {ocrResult.source_type && (
                                <p className="text-xs mt-0.5 opacity-75">
                                  Kaynak: {ocrResult.source_type === 'handwritten' ? 'El yazısı not' : 
                                           ocrResult.source_type === 'machine_screen' ? 'Cihaz ekranı' : 'Bilinmeyen'}
                                </p>
                              )}
                              {ocrResult.notes && (
                                <p className="text-xs mt-0.5 opacity-75">{ocrResult.notes}</p>
                              )}
                            </>
                          ) : (
                            <p>{ocrResult.error || 'Ölçüm okunamadı'}</p>
                          )}
                        </div>
                        <button
                          onClick={() => setOcrResult(null)}
                          className="flex-shrink-0 opacity-50 hover:opacity-100"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                      {/* OCR kaynak görüntüsü küçük önizleme */}
                      {ocrImage && ocrResult.success && (
                        <div className="mt-2 flex items-center gap-2">
                          <img src={ocrImage} alt="OCR kaynak" className="w-16 h-12 object-cover rounded border" />
                          <span className="text-xs opacity-60">Kaynak fotoğraf</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-5">
                    {ALL_FIELD_GROUPS.map((group) => (
                      <div key={group.title}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 border-b pb-1">{group.title}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {group.fields.map((field) => (
                            <div key={field.key} className="space-y-0.5">
                              <Label htmlFor={field.key} className="text-[11px] font-medium">{field.label}</Label>
                              <Input
                                id={field.key}
                                type="number"
                                min={field.min}
                                max={field.max}
                                step="0.1"
                                value={biometrics[field.key] || ''}
                                onChange={(e) => setBiometrics(prev => ({
                                  ...prev,
                                  [field.key]: parseFloat(e.target.value) || 0
                                }))}
                                className="h-8 text-sm"
                                data-testid={`biometric-input-${field.key}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-4 border-t">
                    {hasNoCredits && (
                      <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800" data-testid="credit-warning-banner-2">
                        <strong>Yetersiz kredi.</strong> 4K üretim için bir paket satın almanız gerekiyor.
                      </div>
                    )}
                    <Button
                      onClick={handleScientificEnhance}
                      disabled={enhancing || !hasAnyImage || !allValidated || hasNoCredits}
                      className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] h-12 text-base md:text-lg disabled:opacity-60"
                      data-testid="scientific-enhance-btn"
                    >
                      {enhancing ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {enhanceProgress}
                        </>
                      ) : hasNoCredits ? (
                        <>
                          <Wand2 className="w-5 h-5 mr-2" />
                          Kredi Yetersiz
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-5 h-5 mr-2" />
                          Bilimsel Analiz Başlat
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader className="py-3 px-4 border-b bg-slate-50">
                  <CardTitle className="text-sm">Kaynak Görüntüler</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {sideImage && (
                    <div>
                      <p className="text-xs text-blue-500 mb-1">Profil</p>
                      <img src={sideImage} alt="Profil" className="w-full rounded-lg" />
                    </div>
                  )}
                  {frontImage && (
                    <div>
                      <p className="text-xs text-green-500 mb-1">Frontal</p>
                      <img src={frontImage} alt="Frontal" className="w-full rounded-lg" />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="flex-1 overflow-auto">
            <div className="space-y-4">
              {/* 4K Sonuç Görüntüsü */}
              <Card className="border-0 shadow-lg" data-testid="result-image-card">
                <CardHeader className="py-3 px-4 border-b bg-slate-50">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    4K Sonuç Görüntüsü
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {enhancedImage ? (
                    <div className="relative rounded-xl overflow-hidden bg-slate-950">
                      <img src={enhancedImage} alt="4K Sonuç" className="w-full max-h-[500px] object-contain" />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-white font-semibold text-sm">Ultrason 4K</p>
                          <div className="flex gap-2">
                            <Button
                              onClick={handleDownloadImage}
                              size="sm"
                              variant="secondary"
                              className="bg-white/20 backdrop-blur text-white hover:bg-white/30 border-0"
                              data-testid="download-4k-image"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              İndir
                            </Button>
                            <Button
                              onClick={() => {
                                sessionStorage.setItem('4d_source_image', enhancedImage);
                                navigate('/doctor/4d-simulation?type=ultrasound_4k');
                              }}
                              size="sm"
                              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white border-0"
                              data-testid="4d-from-result"
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              Video Animasyon
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <ImageIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Henüz sonuç yok — önce analiz başlatın</p>
                    </div>
                  )}
                  {enhancedImage && (
                    <div className="mt-4">
                      <ShareActions
                        imageBase64={enhancedImage}
                        patientPhone={patient?.phone}
                        patientName={`${patient?.first_name || ''} ${patient?.last_name || ''}`}
                        generationType="4k"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Doğrulama Sonuçları */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="py-3 px-4 border-b bg-green-50">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-green-600" />
                    Doğrulama Sonuçları
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {validation ? (
                    <>
                      <div className="text-center mb-4 pb-4 border-b">
                        <p className={`text-4xl font-bold ${getScoreColor(validation.overall_match_score)}`}>
                          %{validation.overall_match_score?.toFixed(0)}
                        </p>
                        <p className="text-lg font-medium text-slate-700">{validation.category_tr}</p>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2">Özellik</th>
                              <th className="text-center p-2 bg-blue-50">Hesaplanan</th>
                              <th className="text-center p-2 bg-green-50">Ölçülen</th>
                              <th className="text-center p-2">%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validation.comparisons?.map((comp, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-2 font-medium">{comp.name_tr}</td>
                                <td className="p-2 text-center text-blue-700 font-mono">
                                  {typeof comp.calculated_value === 'number' ? comp.calculated_value.toFixed(3) : comp.calculated_value}
                                </td>
                                <td className="p-2 text-center text-green-700 font-mono">
                                  {typeof comp.measured_value === 'number' ? comp.measured_value.toFixed(3) : comp.measured_value}
                                </td>
                                <td className={`p-2 text-center font-bold ${getScoreColor(comp.match_percentage)}`}>
                                  {comp.match_percentage?.toFixed(0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Henüz sonuç yok</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Processing Indicator */}
        {enhancing && (
          <Card className="border-0 shadow-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white flex-shrink-0">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3">
                <Scan className="w-5 h-5 animate-pulse" />
                <div className="flex-1">
                  <p className="font-medium">{enhanceProgress}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <InsufficientCreditsModal
        open={!!insufficientCredits}
        detail={insufficientCredits}
        onClose={() => setInsufficientCredits(null)}
        onResolved={() => {
          setInsufficientCredits(null);
          toast.success('Kredi yüklendi. Üretimi tekrar başlatabilirsiniz.');
        }}
      />
    </DashboardLayout>
  );
};

export default Ultrasound4KPage;
